import { useEffect, useRef } from 'react';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import type { z } from 'zod';
import type { SchemaLocation } from '@axiom/types';

type SchemaLocation = z.infer<typeof SchemaLocation>;

// GOOGLE_MAPS_API_URL carries the loader config as query params: the api key,
// and the cloud map id that advanced markers need.
const readApiParams = (googleApiUrl?: string | null) => {
  const params = googleApiUrl ? new URL(googleApiUrl).searchParams : undefined;

  return {
    key: params?.get('key') || '',
    mapId: params?.get('map_id') || undefined,
  };
};

// setOptions installs the global loader and only takes effect on the first
// call, so guard it — the hook mounts once per Location field.
let optionsSet = false;
const setLoaderOptions = (key: string) => {
  if (optionsSet) return;
  optionsSet = true;

  setOptions({
    key,
    region: 'US',
    language: 'en',
  });
};

// The persisted `locationAddressComponents.addresses` contract predates the
// Places API renaming long_name/short_name to longText/shortText. The backend
// stores these keys verbatim, so map the new shape back onto the old one.
const toAddressComponents = (
  addressComponents: google.maps.places.AddressComponent[] | undefined
) =>
  addressComponents?.map(({ longText, shortText, types }) => ({
    long_name: longText ?? '',
    short_name: shortText ?? '',
    types,
  }));

const DefaultLocation = {
  locationLatitude: 40.7127753,
  locationLongitude: -74.0059728,
};

export const useGoogleMaps = ({
  googleApiUrl,
  mapRef,
}: {
  googleApiUrl?: string | null;
  mapRef: React.MutableRefObject<unknown>;
}) => {
  const googleMapApiService = useRef<{
    map?: google.maps.Map;
    marker?: google.maps.marker.AdvancedMarkerElement;
    places?: google.maps.PlacesLibrary;
    requests: {
      clear: () => void;
      getPlaceSuggestions: (placeName: string) => Promise<SchemaLocation[]>;
      getPlaceDetails: (placeLocationId: string) => Promise<SchemaLocation>;
    };
    sessionToken?: google.maps.places.AutocompleteSessionToken;
  }>({
    map: undefined,
    marker: undefined,
    places: undefined,
    requests: {
      clear: (): void => {
        const { marker } = googleMapApiService.current;
        if (marker) {
          marker.map = null;
          marker.position = null;
        }
      },
      getPlaceSuggestions: async (
        placeName: string
      ): Promise<SchemaLocation[]> => {
        const { places, sessionToken } = googleMapApiService.current;
        if (!places) return [];

        const { suggestions } =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: placeName,
            sessionToken,
          });

        return suggestions.flatMap(({ placePrediction }) =>
          placePrediction
            ? [
                {
                  locationName: placePrediction.text.text,
                  locationPlaceId: placePrediction.placeId,
                },
              ]
            : []
        );
      },
      getPlaceDetails: async (
        placeLocationId: string
      ): Promise<SchemaLocation> => {
        const { map, marker, places } = googleMapApiService.current;
        if (!places) {
          throw new Error('The Google Maps places library is not loaded.');
        }

        const { place } = await new places.Place({
          id: placeLocationId,
        }).fetchFields({
          fields: ['addressComponents', 'displayName', 'id', 'location'],
        });

        if (place.location) {
          map?.setCenter(place.location);

          if (marker) {
            marker.map = null;
            marker.position = place.location;
          }
        }

        setTimeout(() => {
          if (map && marker) {
            marker.map = map;
          }
        }, 0);

        // A session ends with the details fetch, so start a fresh one.
        googleMapApiService.current.sessionToken =
          new places.AutocompleteSessionToken();

        return {
          locationAddressComponents: {
            addresses: toAddressComponents(place.addressComponents),
          },
          locationName: place.displayName,
          locationLatitude: place.location?.lat(),
          locationLongitude: place.location?.lng(),
          locationPlaceId: place.id,
        };
      },
    },
    sessionToken: undefined,
  });

  useEffect(() => {
    const { key, mapId } = readApiParams(googleApiUrl);
    setLoaderOptions(key);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    Promise.all([
      importLibrary('maps'),
      importLibrary('marker'),
      importLibrary('places'),
    ]).then(([{ Map }, { AdvancedMarkerElement }, placesLibrary]) => {
      const mapInstance = new Map(mapRef.current as HTMLElement, {
        center: {
          lat: DefaultLocation.locationLatitude,
          lng: DefaultLocation.locationLongitude,
        },
        controlSize: 22,
        draggable: false,
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        clickableIcons: false,
        // advanced markers only render on a map with a cloud map id
        mapId,
        mapTypeId: 'roadmap',
        zoom: 13,
        disableDoubleClickZoom: true,
        zoomControl: false,
      });

      googleMapApiService.current.map = mapInstance;
      googleMapApiService.current.marker = new AdvancedMarkerElement({
        map: mapInstance,
      });
      googleMapApiService.current.places = placesLibrary;
      googleMapApiService.current.sessionToken =
        new placesLibrary.AutocompleteSessionToken();
    });
  }, []);

  return googleMapApiService.current;
};
